import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, SmartphoneIcon, BotIcon, LinkIcon, SearchIcon, PackageIcon, ZapIcon, CheckIcon, XIcon, PlayIcon } from 'lucide-react';
const LOOM_VIDEO_ID = '7a85a1937047412cbbda6fc84aee34a1';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const erpLogos = [{
  name: 'Thruline',
  logo: 'https://thruline-public.s3.us-west-1.amazonaws.com/brand/logos/png/logo-dark-trans.png'
}, {
  name: 'Acumatica',
  logo: 'https://cdn.magicpatterns.com/uploads/tAoYHAZBQL5TeHPc36oVJK/accumatica.jpg'
}, {
  name: 'Dynamics 365',
  logo: 'https://cdn.magicpatterns.com/uploads/1youVjT3WnibLysXAj1KQr/dynamics.png'
}, {
  name: 'Epicor',
  logo: 'https://cdn.magicpatterns.com/uploads/437pF2oUvSRzPU4q1q7tA2/epicor.png'
}, {
  name: 'NetSuite',
  logo: 'https://cdn.magicpatterns.com/uploads/3NobZbn61Nz2usBJYJA6gq/netsuite.jpg'
}, {
  name: 'Infor',
  logo: 'https://cdn.magicpatterns.com/uploads/i6TwC5qJLVY6i9We6FbDv5/png-transparent-infor-hd-logo-thumbnail.png'
}];
function ERPLogoMarquee() {
  const allLogos = [...erpLogos, ...erpLogos];
  return <div className="relative mt-10" data-id="element-1478">
      {/* Left fade */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-navy to-transparent z-10 pointer-events-none" data-id="element-1479" />
      {/* Right fade */}
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-navy to-transparent z-10 pointer-events-none" data-id="element-1480" />

      <div className="flex animate-marquee items-center" data-id="element-1481">
        {allLogos.map((erp, index) => <div key={`${erp.name}-${index}`} className="flex-shrink-0 mx-12 flex items-center justify-center" data-id="element-1482">
            <img src={erp.logo} alt={erp.name} className="h-10 w-auto max-w-[180px] object-contain transition-opacity duration-300" style={{
          filter: 'grayscale(1) invert(1)',
          mixBlendMode: 'screen',
          opacity: 0.5
        }} onMouseEnter={e => {
          e.currentTarget.style.opacity = '0.9';
        }} onMouseLeave={e => {
          e.currentTarget.style.opacity = '0.5';
        }} data-id="element-1483" />
          </div>)}
      </div>
    </div>;
}
export function ReceiveProPage() {
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-navy text-white font-sans" data-id="element-1484">
      {/* ── HERO SECTION ── */}
      <section className="relative w-full min-h-screen flex items-center justify-center pt-32 pb-24 px-6 overflow-hidden bg-navy text-center" data-id="element-1485">
        <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-1486" />

        <div className="relative z-10 max-w-4xl mx-auto" data-id="element-1487">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 text-blue-400 text-sm font-medium mb-8" data-id="element-1488">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" data-id="element-1489" />
            Now available as a standalone product
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6" data-id="element-1490">
            Your dock team should be moving pallets,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-br from-blue-400 to-teal-400" data-id="element-1491">
              not typing into an ERP
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-1492">
            ReceivePro turns a photo of a packing slip into a completed
            receiving record. An AI agent handles the matching, the receiving,
            and the ERP sync — so your team can scan and walk away.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12" data-id="element-1493">
            <a href="https://calendly.com/alex-thruline/sales-call" className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl bg-blue-500 text-white font-semibold text-base hover:bg-blue-400 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(59,130,246,0.3)]" data-id="element-1494">
              Get a Demo
            </a>
            <button onClick={() => setShowDemoVideo(true)} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl border border-white/20 text-white font-semibold text-base hover:border-white/40 hover:bg-white/[0.03] transition-all" data-id="element-1495">
              <PlayIcon className="w-4 h-4" data-id="element-1496" />
              See Demo Video
            </button>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="flex items-center justify-center gap-2 text-sm text-white/40" data-id="element-1497">
            <ZapIcon className="w-4 h-4" data-id="element-1498" />
            Works with Thruline, NetSuite, Epicor, SAP, and more — no ERP switch
            required
          </motion.p>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="relative w-full bg-navy-light py-24 px-6" data-id="element-1499">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" data-id="element-1500" />
        <div className="max-w-6xl mx-auto" data-id="element-1501">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1502">
            <motion.div variants={fadeUp} className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-4" data-id="element-1503">
              How It Works
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4" data-id="element-1504">
              Scan the packing slip. Walk to the next box.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/70 max-w-2xl mb-12 leading-relaxed" data-id="element-1505">
              ReceivePro replaces manual data entry with a single photo. The AI
              agent does everything else — automatically.
            </motion.p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1506">
              {[{
              num: '01',
              title: 'Scan',
              desc: "Your dock worker photographs the bill of lading or packing slip with their phone. That's their only job.",
              tag: 'User — 10 seconds',
              tagColor: 'bg-blue-500/10 text-blue-400'
            }, {
              num: '02',
              title: 'Extract',
              desc: 'AI reads the document and pulls out the PO number, line items, quantities, units of measure, and tracking number.',
              tag: 'Agent — automatic',
              tagColor: 'bg-purple-500/10 text-purple-400'
            }, {
              num: '03',
              title: 'Match & Receive',
              desc: 'The agent searches your ERP for the matching PO, reconciles quantities, and creates the receiving transaction — all without human involvement.',
              tag: 'Agent — automatic',
              tagColor: 'bg-purple-500/10 text-purple-400'
            }, {
              num: '04',
              title: 'Sync & Confirm',
              desc: 'The receipt syncs to your ERP. A push notification confirms completion. Your inventory is updated in minutes, not hours.',
              tag: 'System — synced to ERP',
              tagColor: 'bg-teal-500/10 text-teal-400'
            }].map((step, i) => <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 transition-all hover:border-blue-500/30 hover:bg-white/[0.04] hover:-translate-y-1" data-id="element-1507">
                  <div className="font-mono text-xs text-blue-400 bg-blue-500/10 w-8 h-8 rounded-lg flex items-center justify-center mb-4 font-bold" data-id="element-1508">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-semibold mb-2" data-id="element-1509">{step.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed mb-4" data-id="element-1510">
                    {step.desc}
                  </p>
                  <span className={`font-mono text-[10px] px-2.5 py-1 rounded-md inline-block ${step.tagColor}`} data-id="element-1511">
                    {step.tag}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="relative w-full bg-navy py-24 px-6" data-id="element-1512">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" data-id="element-1513" />
        <div className="max-w-6xl mx-auto" data-id="element-1514">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1515">
            <motion.div variants={fadeUp} className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-4" data-id="element-1516">
              What ReceivePro Does
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4" data-id="element-1517">
              Everything between the dock and the ERP — automated
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/70 max-w-2xl mb-12 leading-relaxed" data-id="element-1518">
              ReceivePro isn't another WMS module. It's a focused tool that does
              one thing exceptionally well: turn paper receipts into ERP
              transactions.
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1519">
              {[{
              icon: SmartphoneIcon,
              title: 'Mobile-First Capture',
              desc: 'Full-screen camera viewfinder optimized for warehouse environments. Large touch targets for gloved hands. Flash for low-light docks. Scan box after box without waiting.',
              iconBg: 'bg-blue-500/10 text-blue-400'
            }, {
              icon: BotIcon,
              title: 'AI Agent Matching',
              desc: "The agent searches across your purchasing systems using a cascading match strategy — PO number, vendor + parts, tracking number. It doesn't just look up; it reasons.",
              iconBg: 'bg-purple-500/10 text-purple-400'
            }, {
              icon: LinkIcon,
              title: 'Cross-System Record Linking',
              desc: 'Every receiving record links to the PO, Purchase Request, Receipt Ticket, Transportation Ticket, and Carrier Tracking — all clickable, all in one view.',
              iconBg: 'bg-teal-500/10 text-teal-400'
            }, {
              icon: SearchIcon,
              title: 'Inspection Notices',
              desc: 'When a received item requires quality inspection, ReceivePro auto-generates an inspection notice and notifies your QA team. No manual handoff, no missed inspections.',
              iconBg: 'bg-amber-500/10 text-amber-400'
            }, {
              icon: PackageIcon,
              title: 'Carrier Tracking Enrichment',
              desc: 'The agent queries UPS, FedEx, USPS, and DHL using the extracted tracking number. Delivery confirmation, package count, signed-by name, and proof of delivery — pulled automatically.',
              iconBg: 'bg-blue-500/10 text-blue-400'
            }, {
              icon: ZapIcon,
              title: 'Multi-Scan Queue',
              desc: 'Dock workers scan multiple shipments in rapid succession. Each scan enters the pipeline independently. The camera comes right back — no waiting on processing screens.',
              iconBg: 'bg-purple-500/10 text-purple-400'
            }].map((feature, i) => {
              const IconComp = feature.icon;
              return <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 transition-all hover:border-blue-500/30 hover:-translate-y-1" data-id="element-1520">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${feature.iconBg}`} data-id="element-1521">
                      <IconComp className="w-5 h-5" data-id="element-1522" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2" data-id="element-1523">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-white/70 leading-relaxed" data-id="element-1524">
                      {feature.desc}
                    </p>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── THE NUMBERS / ROI ── */}
      <section className="relative w-full bg-navy-light py-24 px-6 text-center" data-id="element-1525">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" data-id="element-1526" />
        <div className="max-w-5xl mx-auto" data-id="element-1527">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1528">
            <motion.div variants={fadeUp} className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-4" data-id="element-1529">
              The Numbers
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4" data-id="element-1530">
              Manual receiving is costing you more than you think
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/70 mb-16 max-w-2xl mx-auto leading-relaxed" data-id="element-1531">
              At 25 shipments a day and 10 minutes saved per shipment,
              ReceivePro pays for itself in the first week.
            </motion.p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1532">
              {[{
              num: '10s',
              color: 'text-blue-400',
              label: 'Per shipment',
              sub: 'was 8–15 min'
            }, {
              num: '<15m',
              color: 'text-teal-400',
              label: 'Dock-to-stock',
              sub: 'was 2–6 hours'
            }, {
              num: '98%',
              color: 'text-green-400',
              label: '3-way match rate',
              sub: 'was 78–88%'
            }, {
              num: '<0.5%',
              color: 'text-amber-400',
              label: 'Error rate',
              sub: 'was 3–8%'
            }].map((stat, i) => <motion.div key={i} variants={fadeUp} className="p-4" data-id="element-1533">
                  <div className={`font-mono text-4xl md:text-5xl font-bold mb-2 ${stat.color}`} data-id="element-1534">
                    {stat.num}
                  </div>
                  <div className="text-sm text-white/70 font-medium" data-id="element-1535">
                    {stat.label}
                  </div>
                  <div className="text-xs text-white/40 mt-1" data-id="element-1536">{stat.sub}</div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── INTEGRATIONS ── */}
      <section id="integrations" className="relative w-full bg-navy py-24 px-6 text-center overflow-hidden" data-id="element-1537">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" data-id="element-1538" />
        <div className="max-w-4xl mx-auto" data-id="element-1539">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1540">
            <motion.div variants={fadeUp} className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-4" data-id="element-1541">
              Integrations
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4" data-id="element-1542">
              Works with your ERP — whatever it is
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/70 mb-4 max-w-2xl mx-auto leading-relaxed" data-id="element-1543">
              ReceivePro is built as a companion to Thruline, but it connects to
              any system of record. You don't need to switch ERPs to use it.
            </motion.p>
          </motion.div>
        </div>

        <ERPLogoMarquee data-id="element-1544" />
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="relative w-full bg-navy-light py-24 px-6" data-id="element-1545">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" data-id="element-1546" />
        <div className="max-w-6xl mx-auto" data-id="element-1547">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} className="text-center mb-16" data-id="element-1548">
            <motion.div variants={fadeUp} className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-4" data-id="element-1549">
              Pricing
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4" data-id="element-1550">
              Simple pricing that scales with your dock
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed" data-id="element-1551">
              Every plan includes the full AI agent pipeline, carrier tracking
              enrichment, Expected Receipts, and inspection notices. No feature
              gating.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-1552">
            {/* Starter */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 flex flex-col" data-id="element-1553">
              <div className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-1" data-id="element-1554">
                Starter
              </div>
              <div className="text-white/50 text-sm mb-4" data-id="element-1555">Single Location</div>
              <div className="mb-1" data-id="element-1556">
                <span className="text-4xl font-bold text-white" data-id="element-1557">$499</span>
                <span className="text-white/40 text-base ml-1" data-id="element-1558">/month</span>
              </div>
              <div className="text-xs text-white/40 mb-6" data-id="element-1559">
                Includes 450 scans/month$1.50 per additional scan
              </div>
              <hr className="border-white/[0.06] mb-6" data-id="element-1560" />
              <ul className="space-y-3 flex-1 mb-8" data-id="element-1561">
                {[{
                text: '1 location',
                included: true
              }, {
                text: 'Up to 5 users',
                included: true
              }, {
                text: '450 scans/month included',
                included: true
              }, {
                text: 'Full AI extraction + auto-receive',
                included: true
              }, {
                text: '1 ERP integration (Thruline, NetSuite, SAP B1, Epicor, or other)',
                included: true
              }, {
                text: 'Carrier tracking enrichment',
                included: true
              }, {
                text: 'Expected Receipts',
                included: true
              }, {
                text: 'Quality inspection notices',
                included: true
              }, {
                text: 'Email + chat support',
                included: true
              }, {
                text: 'Multi-location support',
                included: false
              }, {
                text: 'Custom integrations',
                included: false
              }].map((item, i) => <li key={i} className={`flex items-start gap-2.5 text-sm ${item.included ? 'text-white/70' : 'text-white/25'}`} data-id="element-1562">
                    {item.included ? <CheckIcon className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1563" /> : <XIcon className="w-4 h-4 text-white/20 mt-0.5 flex-shrink-0" data-id="element-1564" />}
                    {item.text}
                  </li>)}
              </ul>
              <button onClick={() => setShowDemoVideo(true)} className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl border border-white/20 text-white font-semibold text-sm hover:border-white/40 hover:bg-white/[0.03] transition-all" data-id="element-1565">
                <PlayIcon className="w-4 h-4" data-id="element-1566" />
                See Demo Video
              </button>
            </motion.div>

            {/* Professional — Featured */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-blue-500/30 bg-blue-500/[0.06] p-8 flex flex-col relative" data-id="element-1567">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-blue-500 text-white text-[10px] font-bold tracking-wider uppercase" data-id="element-1568">
                Most Popular
              </div>
              <div className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-1" data-id="element-1569">
                Professional
              </div>
              <div className="text-white/50 text-sm mb-4" data-id="element-1570">Multi-Location</div>
              <div className="mb-1" data-id="element-1571">
                <span className="text-4xl font-bold text-white" data-id="element-1572">$999</span>
                <span className="text-white/40 text-base ml-1" data-id="element-1573">/month</span>
              </div>
              <div className="text-xs text-white/40 mb-6" data-id="element-1574">
                Unlimited scans included
              </div>
              <hr className="border-blue-500/20 mb-6" data-id="element-1575" />
              <ul className="space-y-3 flex-1 mb-8" data-id="element-1576">
                {['Up to 5 locations', 'Up to 25 users', 'Unlimited scans', 'Full AI extraction + auto-receive', 'Up to 3 ERP / system integrations', 'Carrier tracking enrichment', 'Expected Receipts + bulk CSV import', 'Quality inspection notices', 'Multi-system PO matching (ERP + procurement + TMS)', 'Linked records (PO, PR, receipt ticket, transport ticket)', 'Priority support + onboarding'].map((item, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-white/70" data-id="element-1577">
                    <CheckIcon className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1578" />
                    {item}
                  </li>)}
              </ul>
              <a href="https://calendly.com/alex-thruline/sales-call" className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl bg-blue-500 text-white font-semibold text-sm hover:bg-blue-400 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(59,130,246,0.3)]" data-id="element-1579">
                Get a Demo
              </a>
            </motion.div>

            {/* Enterprise */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 flex flex-col" data-id="element-1580">
              <div className="font-mono text-xs text-blue-400 tracking-[0.1em] uppercase mb-1" data-id="element-1581">
                Enterprise
              </div>
              <div className="text-white/50 text-sm mb-4" data-id="element-1582">Custom</div>
              <div className="mb-1" data-id="element-1583">
                <span className="text-4xl font-bold text-white" data-id="element-1584">Custom</span>
              </div>
              <div className="text-xs text-white/40 mb-6" data-id="element-1585">
                For complex operations with advanced requirements
              </div>
              <hr className="border-white/[0.06] mb-6" data-id="element-1586" />
              <ul className="space-y-3 flex-1 mb-8" data-id="element-1587">
                {['Unlimited locations', 'Unlimited users', 'Unlimited scans', 'Full AI extraction + auto-receive', 'Unlimited integrations + custom connectors', 'Carrier tracking enrichment', 'Expected Receipts + ASN auto-import', 'All inspection notice types', 'Custom approval workflows', 'Dedicated onboarding + SLA', 'SSO / SAML + audit logging'].map((item, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-white/70" data-id="element-1588">
                    <CheckIcon className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1589" />
                    {item}
                  </li>)}
              </ul>
              <a href="https://calendly.com/alex-thruline/sales-call" className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl border border-white/20 text-white font-semibold text-sm hover:border-white/40 hover:bg-white/[0.03] transition-all" data-id="element-1590">
                Talk to Sales
              </a>
            </motion.div>
          </motion.div>

          {/* ROI comparison note */}
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-sm text-white/40 text-center max-w-3xl mx-auto leading-relaxed" data-id="element-1591">
            <strong className="text-white/60" data-id="element-1592">The math is simple:</strong> at 25
            shipments/day and 10 minutes saved per shipment, ReceivePro saves ~4
            hours of dock labor daily. At $25/hour fully loaded, that's
            $2,200/month in labor alone — before counting error reduction,
            faster three-way match, and inventory accuracy. The Starter plan
            pays for itself in the first week.
          </motion.p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative w-full bg-navy py-32 px-6 text-center overflow-hidden" data-id="element-1593">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" data-id="element-1594" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-blue-500/5 pointer-events-none" data-id="element-1595" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} variants={stagger} className="relative z-10 max-w-3xl mx-auto" data-id="element-1596">
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-bold tracking-tight mb-6" data-id="element-1597">
            Get something built today
          </motion.h2>
          <motion.p variants={fadeUp} className="text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-1598">
            See ReceivePro with your own PO data. We'll set up a pilot in days,
            not months.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1599">
            <a href="https://calendly.com/alex-thruline/sales-call" className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl bg-blue-500 text-white font-semibold text-base hover:bg-blue-400 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(59,130,246,0.3)]" data-id="element-1600">
              Get a Demo
            </a>
            <button onClick={() => setShowDemoVideo(true)} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl border border-white/20 text-white font-semibold text-base hover:border-white/40 hover:bg-white/[0.03] transition-all" data-id="element-1601">
              <PlayIcon className="w-4 h-4" data-id="element-1602" />
              See Demo Video
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* ── LOOM VIDEO MODAL ── */}
      <AnimatePresence data-id="element-1603">
        {showDemoVideo && <>
            <motion.div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowDemoVideo(false)} data-id="element-1604" />
            <motion.div className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1605">
              <motion.div className="relative w-full max-w-5xl pointer-events-auto" initial={{
            scale: 0.95,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.25,
            ease: [0.16, 1, 0.3, 1]
          }} onClick={e => e.stopPropagation()} data-id="element-1606">
                <button onClick={() => setShowDemoVideo(false)} className="absolute -top-12 right-0 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" aria-label="Close video" data-id="element-1607">
                  <XIcon className="w-6 h-6 text-white" data-id="element-1608" />
                </button>
                <div className="relative rounded-2xl overflow-hidden bg-navy border border-white/10 shadow-2xl" data-id="element-1609">
                  <div className="aspect-video" data-id="element-1610">
                    <iframe src={`https://www.loom.com/embed/${LOOM_VIDEO_ID}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`} frameBorder="0" allowFullScreen className="w-full h-full" data-id="element-1611" />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}